import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import axios from 'axios'
import { validateImageFile } from '../utils/profileManager'

import { API_BASE_URL } from '../config/api'

export const useStampForm = () => {
  const navigate = useNavigate()
  const [currentStep, setCurrentStep] = useState('select-event')

  // State for event selection
  const [userEvents, setUserEvents] = useState([])
  const [selectedEvent, setSelectedEvent] = useState(null)

  // State for photo upload
  const [selectedFile, setSelectedFile] = useState(null)
  const [previewUrl, setPreviewUrl] = useState('')
  const [uploading, setUploading] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')

  // Load user's existing events on component mount
  useEffect(() => {
    fetchUserEvents()
  }, [])

  const fetchUserEvents = async () => {
    try {
      // The stamp picker only lists events without a stamp yet, so it wants a
      // generous page rather than the default.
      const response = await axios.get(`${API_BASE_URL}/api/events/my-events?limit=100`, {
        withCredentials: true
      })
      setUserEvents(response.data.events || [])
    } catch (err) {
      if (process.env.NODE_ENV === 'development') console.error('Error fetching user events:', err)
      setError('Failed to load your events')
    }
  }

  const handleEventSelect = (event) => {
    setSelectedEvent(event)
    setCurrentStep('upload-photo')
    setError('')
    setSuccess('')
  }

  const handleFileSelect = (event) => {
    const file = event.target.files[0]
    if (!file) return

    setError('')

    // Validate file
    const validation = validateImageFile(file)
    if (!validation.valid) {
      setError(validation.error)
      return
    }

    setSelectedFile(file)

    // Create preview URL
    const url = URL.createObjectURL(file)
    setPreviewUrl(url)
  }

  const handleStampSubmit = async () => {
    if (!selectedFile || !selectedEvent) return

    setUploading(true)
    setError('')
    setSuccess('')

    try {
      const existingEvent = userEvents.find(e => e._id === selectedEvent.id)

      if (!existingEvent) {
        setError('Event not found in your collection.')
        return
      }

      // Sent as multipart, not as a base64 field. Inline base64 inflated the
      // stored document and every subsequent passport fetch by ~33% of the
      // original image on top of the image itself.
      const formData = new FormData()
      formData.append('stamp', selectedFile)

      await axios.post(`${API_BASE_URL}/api/events/${existingEvent._id}/stamp`, formData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' }
      })

      setSuccess('Stamp added to your existing event successfully!')

      // Navigate back to passport after short delay
      setTimeout(() => {
        navigate('/passport')
      }, 2000)

    } catch (err) {
      if (process.env.NODE_ENV === 'development') console.error('Stamp submission error:', err)
      setError(err.response?.data?.message || 'Failed to add stamp. Please try again.')
    } finally {
      setUploading(false)
    }
  }

  const resetToEventSelection = () => {
    setCurrentStep('select-event')
    setSelectedEvent(null)
    setSelectedFile(null)
    setPreviewUrl('')
    setError('')
    setSuccess('')
  }

  return {
    currentStep,
    userEvents,
    selectedEvent,
    selectedFile,
    previewUrl,
    uploading,
    error,
    success,
    handleEventSelect,
    handleFileSelect,
    handleStampSubmit,
    resetToEventSelection
  }
}